import { useCallback, useEffect, useState } from 'react';
import { handleEvent, JazzRoomParticipant } from '@salutejs/jazz-sdk-web';
import { useQuery } from './useQuery';
import { useSortedParticipants } from './useSortedParticipants';
import { useSounds } from './useSounds';

export const useParticipants = (room) => {
    const localParticipant = useQuery(room.localParticipant);
    const [ remoteParticipant, setRemoteParticipant ] = useState<JazzRoomParticipant | null>(null);

    const [ participants, setParticipants ] = useState<JazzRoomParticipant[]>([]);
    const { sortedParticipants } = useSortedParticipants({room, participants});
    const { playConnect, playDisconnect } = useSounds();

    const onParticipants = useCallback((event) => {
        const { payload: { participants } } = event;
        setParticipants(participants);
    }, []);
    const onParticipantJoined = useCallback((event) => {
        const { payload: { participant } } = event;
        setParticipants((participants) => ([
            ...participants,
            participant
        ]));
        playConnect();
    }, []);
    const onParticipantLeft = useCallback((event) => {
        const { payload: { participant } } = event;
        setParticipants((participants) =>
            participants.filter((p) => p.id !== participant.id),
        );
        playDisconnect();
    }, []);
    const onParticipantUpdate = useCallback((event) => {
        const { payload: { participant } } = event;
        setParticipants((participants) => {
            const index = participants.findIndex(
                (item) => item.id === participant.id,
            );
            if (index === -1) return participants;
            return [
                ...participants.slice(0, index),
                participant,
                ...participants.slice(index + 1)
            ];
        });
    }, []);

    useEffect(() => {
        const participants = room.participants.get();
        const { event$ } = room;
        setParticipants(participants);

        const unsubscribeParticipantsChanged = handleEvent(event$, 'participants', onParticipants);
        const unsubscribeJoined = handleEvent(event$, 'participantJoined', onParticipantJoined);
        const unsubscribeLeft = handleEvent(event$, 'participantLeft', onParticipantLeft);
        const unsubscribeUpdate = handleEvent(event$, 'participantUpdate', onParticipantUpdate);

        return () => {
            unsubscribeParticipantsChanged();
            unsubscribeJoined();
            unsubscribeLeft();
            unsubscribeUpdate();
        }
    }, [room]);

    useEffect(() => {
        const remoteParticipant = sortedParticipants.find(participant => {
            return participant.id !== localParticipant?.id && participant.name !== localParticipant?.name
        });
        if (remoteParticipant) {
            setRemoteParticipant(remoteParticipant);
        } else {
            setRemoteParticipant(null);
        }
    }, [sortedParticipants, localParticipant]);

    return { localParticipantId: localParticipant?.id, remoteParticipantId: remoteParticipant?.id };
}
